import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useNotifications } from "@/context/NotificationContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Bell, BellOff, MessageCircle, ShoppingBag, Heart, Star, CheckCircle2, Search, X } from "lucide-react";
import { toast } from "sonner";

const iconFor = (type) => {
  const props = { size: 20, strokeWidth: 2.5 };
  switch (type) {
    case "new_message": return <MessageCircle {...props} />;
    case "new_sale": return <ShoppingBag {...props} />;
    case "payment_confirmed": return <CheckCircle2 {...props} />;
    case "new_favorite": return <Heart {...props} />;
    case "new_review": return <Star {...props} />;
    default: return <Bell {...props} />;
  }
};

const colorFor = (type) => {
  switch (type) {
    case "new_sale":
    case "payment_confirmed": return "bg-[color:var(--color-mint)]";
    case "new_favorite":
    case "new_message": return "bg-[color:var(--color-yellow)]";
    case "new_review": return "bg-[color:var(--color-coral)] text-white";
    default: return "bg-[color:var(--color-surface)]";
  }
};

export default function Notifications() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { notifications, markAllRead, subscribePush, unsubscribePush, permission, refresh } = useNotifications() || {};
  const [searches, setSearches] = useState([]);

  useEffect(() => {
    if (user) api.get("/saved-searches").then(r => setSearches(r.data)).catch(() => {});
  }, [user]);

  const deleteSearch = async (id) => {
    try {
      await api.delete(`/saved-searches/${id}`);
      setSearches(s => s.filter(x => x.search_id !== id));
      toast.success("Alerte supprimée");
    } catch {}
  };

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [user, loading, navigate]);
  useEffect(() => { if (user && markAllRead) markAllRead(); /* mark as read on open */ /* eslint-disable-next-line */}, [user]);

  const enable = async () => {
    try {
      const r = await subscribePush();
      if (r.ok) toast.success("Notifs activées 🔔");
      else if (r.reason === "denied") toast.error("Active les notifs dans les réglages (Réglages iOS > Fripri ou ton navigateur)");
      else if (r.reason === "timeout" || r.reason === "native-error") toast.error("L'enregistrement des notifications n'a pas abouti — réessaie après la prochaine mise à jour de l'app");
      else toast.error("Non supporté sur ce navigateur");
    } catch { toast.error("Erreur"); }
  };

  const disable = async () => {
    try { await unsubscribePush(); toast.success("Notifs désactivées"); }
    catch {}
  };

  if (!user) return <div className="p-20 text-center">Chargement…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-6 lg:py-10 fade-in has-bottom-nav" data-testid="notifications-page">
      <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
        <div>
          <p className="label mb-2">Notifications</p>
          <h1 className="text-3xl lg:text-5xl font-black tracking-tighter">Ton fil d'actu</h1>
        </div>
        {permission === "granted" ? (
          <button onClick={disable} className="btn btn-ghost btn-sm" data-testid="disable-push-btn">
            <BellOff size={14}/> Désactiver
          </button>
        ) : (
          <button onClick={enable} className="btn btn-mint btn-sm" data-testid="enable-push-btn">
            <Bell size={14}/> Activer les notifs
          </button>
        )}
      </div>

      {permission !== "granted" && permission !== "denied" && (
        <div className="card border-2 border-black bg-[color:var(--color-yellow)] p-4 mb-6 flex items-center gap-3" data-testid="push-cta-banner">
          <Bell size={28} strokeWidth={2.5} />
          <div className="flex-1">
            <p className="font-black tracking-tight">Active les notifications</p>
            <p className="text-xs">Sois averti dès qu'une vente, un message ou un avis arrive.</p>
          </div>
        </div>
      )}

      {searches.length > 0 && (
        <div className="mb-8" data-testid="saved-searches-section">
          <p className="label font-bold mb-3">Mes alertes de recherche</p>
          <div className="space-y-2">
            {searches.map(s => (
              <div key={s.search_id} className="flex items-center justify-between gap-3 p-3 border-2 border-black" data-testid={`saved-search-${s.search_id}`}>
                <p className="font-bold text-sm truncate flex items-center gap-2"><Search size={14} strokeWidth={2.5} /> {s.label}</p>
                <button onClick={() => deleteSearch(s.search_id)} className="w-8 h-8 flex items-center justify-center shrink-0" data-testid={`delete-search-${s.search_id}`}><X size={16}/></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!notifications || notifications.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-black">
          <Bell size={32} strokeWidth={2} className="mx-auto mb-4"/>
          <p className="text-2xl font-black mb-2">C'est calme ici</p>
          <p className="text-sm text-[color:var(--color-text-muted)]">Tes notifs apparaîtront dès qu'il se passe quelque chose.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map((n, i) => (
            <Link key={i} to={n.url || "/"} className={`flex items-start gap-3 p-4 border-2 border-black ${n.read ? 'bg-white' : 'bg-[color:var(--color-surface)]'} card-hover`} data-testid={`notif-${i}`}>
              <div className={`w-10 h-10 shrink-0 border-2 border-black flex items-center justify-center ${colorFor(n.type)}`}>
                {iconFor(n.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-bold truncate">{n.title}</p>
                  <p className="label shrink-0">{new Date(n.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}</p>
                </div>
                <p className="text-sm text-[color:var(--color-text-muted)] mt-0.5">{n.body}</p>
              </div>
              {!n.read && <div className="w-2 h-2 bg-[color:var(--color-coral)] rounded-full mt-2"/>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
